class HeroSlideshow {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-hero-track]');
    this.allSlides = Array.from(el.querySelectorAll('[data-hero-slide]'));
    this.allDots = Array.from(el.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = el.querySelector('[data-hero-prev]');
    this.nextBtn = el.querySelector('[data-hero-next]');
    this.index = 0;
    this.autoplay = el.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(el.dataset.autoplaySpeed, 10) || 6000;
    this.timer = null;
    this.resizeTimer = null;

    // Drag/press state
    this.isPointerDown = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartTime = 0;
    this.dragDeltaPercent = 0;
    this.pointerId = null;
    this.wasAutoplayingBeforePress = false;
    this.suppressNextClick = false;

    // slides/dots hidden at the current breakpoint (e.g. "hide on desktop")
    // are excluded from rotation entirely, not just visually skipped.
    this.refreshVisible();

    if (this.slides.length <= 1) return;

    this.bindEvents();
    this.startAutoplay();

    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.handleResize(), 150);
    });
  }

  refreshVisible() {
    this.slides = this.allSlides.filter((slide) => getComputedStyle(slide).display !== 'none');
    this.dots = this.allDots.filter((dot) => getComputedStyle(dot).display !== 'none');
  }

  handleResize() {
    const prevCount = this.slides.length;
    this.refreshVisible();

    if (this.slides.length !== prevCount) {
      // Visible set changed (crossed the mobile/desktop breakpoint) — snap
      // back to the first visible slide without an animated jump.
      this.index = 0;
      this.track.style.transition = 'none';
      this.go(0);
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetHeight; // force reflow so the transition removal takes effect
      this.track.style.transition = '';
    }

    if (this.slides.length <= 1) {
      this.stopAutoplay();
    } else if (this.autoplay) {
      this.startAutoplay();
    }
  }

  bindEvents() {
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.go(this.index + 1);
        this.restartAutoplay();
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.go(this.index - 1);
        this.restartAutoplay();
      });
    }

    this.el.addEventListener('click', (e) => {
      const dot = e.target.closest('[data-hero-dot]');
      if (!dot || !this.dots.includes(dot)) return;
      this.go(this.dots.indexOf(dot));
      this.restartAutoplay();
    });

    // Desktop mouse hover still pauses, independent of press/drag below.
    this.el.addEventListener('mouseenter', () => this.stopAutoplay());
    this.el.addEventListener('mouseleave', () => {
      if (!this.isPointerDown) this.startAutoplay();
    });

    // Press-and-hold pause + live drag-follow swipe, unified for touch and mouse.
    // pan-y lets the page still scroll vertically with one finger; only the
    // horizontal component of the gesture is claimed by this component, and
    // it's a CSS-level hint so the browser's compositor can handle it without
    // waiting on the main thread — cheaper than manual preventDefault on touchmove.
    this.track.style.touchAction = 'pan-y';

    this.track.addEventListener('pointerdown', (e) => {
      if (e.target.closest('[data-hero-prev], [data-hero-next], [data-hero-dot]')) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.dragStartX = e.clientX;
      this.dragStartTime = performance.now();
      this.dragDeltaPercent = 0;
      this.pointerId = e.pointerId;
      this.wasAutoplayingBeforePress = !!this.timer;

      // Holding the slide pauses rotation immediately, even before any movement.
      this.stopAutoplay();

      this.track.style.transition = 'none';
      try { this.track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    });

    this.track.addEventListener('pointermove', (e) => {
      if (!this.isPointerDown || e.pointerId !== this.pointerId) return;

      const diffX = e.clientX - this.dragStartX;
      const width = this.el.getBoundingClientRect().width || 1;
      this.dragDeltaPercent = (diffX / width) * 100;

      if (!this.isDragging && Math.abs(diffX) > 4) {
        this.isDragging = true;
      }

      if (this.isDragging) {
        // Let the track visually follow the finger/cursor partway — a real
        // "half swipe" instead of an all-or-nothing gesture.
        const total = this.slides.length;
        let clamped = this.dragDeltaPercent;
        // Add resistance at the very ends so it doesn't drag away into empty space.
        if ((this.index === 0 && clamped > 0) || (this.index === total - 1 && clamped < 0)) {
          clamped *= 0.35;
        }
        this.track.style.transform = `translateX(calc(-${this.index * 100}% + ${clamped}%))`;
      }
    });

    const endPress = (e) => {
      if (!this.isPointerDown || (this.pointerId !== null && e.pointerId !== this.pointerId)) return;

      this.isPointerDown = false;
      this.track.style.transition = '';

      if (this.isDragging) {
        const width = this.el.getBoundingClientRect().width || 1;
        const diffPx = (this.dragDeltaPercent / 100) * width;
        const duration = Math.max(performance.now() - this.dragStartTime, 1);
        const velocity = Math.abs(diffPx) / duration; // px per ms

        const DISTANCE_THRESHOLD_PERCENT = 15;
        // A quick short flick (common on mobile) should complete the swipe
        // even if it didn't cross the distance threshold — matching how
        // native scroll views and most mobile carousels behave.
        const FLICK_VELOCITY = 0.35; // px/ms
        const FLICK_MIN_DISTANCE = 20; // px — guards against a light tap registering as a flick

        const metDistance = Math.abs(this.dragDeltaPercent) >= DISTANCE_THRESHOLD_PERCENT;
        const metFlick = velocity >= FLICK_VELOCITY && Math.abs(diffPx) >= FLICK_MIN_DISTANCE;

        if (metDistance || metFlick) {
          // A completed swipe may end on top of a link/button in the slide
          // content — suppress the synthetic click so it doesn't accidentally
          // activate whatever was under the finger when the drag started.
          this.suppressNextClick = true;
          this.go(this.dragDeltaPercent < 0 ? this.index + 1 : this.index - 1);
        } else {
          // Didn't drag far or fast enough — snap back to the current slide.
          this.go(this.index);
        }
      }

      this.isDragging = false;
      this.dragDeltaPercent = 0;

      try { this.track.releasePointerCapture(this.pointerId); } catch (err) { /* noop */ }
      this.pointerId = null;

      // Resume rotation after release, matching whatever state it was in before the press.
      if (this.wasAutoplayingBeforePress) this.startAutoplay();
    };

    this.track.addEventListener('pointerup', endPress);
    this.track.addEventListener('pointercancel', endPress);
    this.track.addEventListener('pointerleave', (e) => {
      // Only treat leaving as a release if the pointer isn't captured
      // (captured pointers keep firing move/up even outside the element).
      if (this.isPointerDown && this.pointerId === null) endPress(e);
    });

    // Prevent the click that fires right after a completed drag-swipe from
    // being interpreted as a click on a link/button inside the slide.
    this.track.addEventListener('click', (e) => {
      if (this.suppressNextClick) {
        e.preventDefault();
        e.stopPropagation();
        this.suppressNextClick = false;
      }
    }, true);
  }

  go(newIndex) {
    const total = this.slides.length;
    if (!total) return;
    this.index = ((newIndex % total) + total) % total;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    });
  }

  startAutoplay() {
    if (!this.autoplay || this.timer || this.slides.length <= 1) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

function initHeroSlideshows() {
  document.querySelectorAll('[data-hero-slideshow]').forEach((el) => {
    if (!el.dataset.heroInitialized) {
      el.dataset.heroInitialized = 'true';
      new HeroSlideshow(el);
    }
  });
}

document.addEventListener('DOMContentLoaded', initHeroSlideshows);

// Re-init when the section is added/updated via the theme editor
document.addEventListener('shopify:section:load', (event) => {
  const slideshow = event.target.querySelector('[data-hero-slideshow]');
  if (slideshow) {
    delete slideshow.dataset.heroInitialized;
    new HeroSlideshow(slideshow);
  }
});
